import { FilterDefinition, FiltersRegistry } from "../types";

// Update a filter inside the registry using a custom updater function
export const updateFilterInRegistry = (
	registry: FiltersRegistry,
	filterId: string,
	updater: (filter: FilterDefinition) => FilterDefinition
): FiltersRegistry => {
	const current = registry.byId[filterId];

	// If filter does not exist, return original registry
	if (!current) {
		return registry;
	}

	const updated = updater(current);

	return {
		...registry,

		// update items list
		items: registry.items.map((item) => (item.id === filterId ? updated : item)),

		// update lookup map
		byId: {
			...registry.byId,
			[filterId]: updated,
		},

		// update favorites group if needed
		groups: {
			...registry.groups,

			favorites: updated.favorite
				? [...new Set([...registry.groups.favorites, filterId])]
				: registry.groups.favorites.filter((id) => id !== filterId),
		},
	};
};

// Rename a filter inside the registry
export const renameFilterInRegistry = (
	registry: FiltersRegistry,
	filterId: string,
	newName: string
): FiltersRegistry => {
	const current = registry.byId[filterId];

	// If filter does not exist, return original registry
	if (!current) {
		return registry;
	}

	const updated: FilterDefinition = {
		...current,
		name: newName,
	};

	return {
		...registry,

		// update items list
		items: registry.items.map((item) => (item.id === filterId ? updated : item)),

		// update lookup map
		byId: {
			...registry.byId,
			[filterId]: updated,
		},
	};
};

// Delete a filter from registry completely
export const deleteFilterFromRegistry = (registry: FiltersRegistry, filterId: string): FiltersRegistry => {
	const nextById = { ...registry.byId };

	// remove from lookup map
	delete nextById[filterId];

	return {
		...registry,

		// remove from items
		items: registry.items.filter((item) => item.id !== filterId),

		// update lookup map
		byId: nextById,

		// remove from all groups
		groups: {
			available: registry.groups.available.filter((id) => id !== filterId),
			saved: registry.groups.saved.filter((id) => id !== filterId),
			configurations: registry.groups.configurations.filter((id) => id !== filterId),
			favorites: registry.groups.favorites.filter((id) => id !== filterId),
		},
	};
};

// Add a custom filter to registry
export const addCustomFilterToRegistry = (registry: FiltersRegistry, filter: FilterDefinition): FiltersRegistry => {
	const group = filter.group;

	// safety check
	if (!group) return registry;

	return {
		...registry,

		// add to items list
		items: [...registry.items, filter],

		// add to lookup map
		byId: {
			...registry.byId,
			[filter.id]: { ...filter },
		},

		// add to correct group
		groups: {
			...registry.groups,
			[group]: [...registry.groups[group], filter.id],
		},
	};
};
